import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Building2, Plus, X, Power, PowerOff, Edit2, Users, ClipboardList } from 'lucide-react';
import { safeConfirm } from '../../lib/confirm';

/**
 * Platform-admin clinic management. Only accessible to users with role=platform_admin.
 * Lists every clinic on the platform with quick suspend/activate, edit, and stats.
 */
export default function PlatformClinics() {
  const [clinics, setClinics] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/platform/clinics').then(r => setClinics(r.data));
  useEffect(() => { load(); }, []);

  const toggleActive = async (c) => {
    if (!await safeConfirm(`${c.is_active ? 'Suspend' : 'Reactivate'} ${c.name}?`)) return;
    setBusy(true);
    try {
      await api.post(`/platform/clinics/${c.clinic_id}/suspend?active=${!c.is_active}`);
      load();
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">PLATFORM</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Clinics on Physiogenic</h1>
          <p className="text-fg-muted mt-2">All tenant clinics — suspend, edit branding, change plan tier.</p>
        </div>
        <button onClick={() => setEdit({ name: '', plan: 'free', country_code: 'PL' })} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="new-clinic-btn">
          <Plus className="w-4 h-4" /> New clinic
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {clinics.map(c => (
          <div key={c.clinic_id} className="surface border border-soft rounded-xl p-4" data-testid={`clinic-${c.clinic_id}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center text-brand">
                  <Building2 className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-heading font-semibold">{c.name}</div>
                  <div className="text-[11px] text-fg-muted font-mono">{c.slug}</div>
                </div>
              </div>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded font-semibold ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {c.is_active ? 'Active' : 'Suspended'}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs mb-3">
              <span className="badge-neutral uppercase">{c.plan}</span>
              {c.country_code && <span className="badge-neutral">{c.country_code}</span>}
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs border-t border-soft pt-3">
              <div className="flex items-center gap-1.5 text-fg-muted">
                <Users className="w-3 h-3" /> {c.user_count} user{c.user_count !== 1 ? 's' : ''}
              </div>
              <div className="flex items-center gap-1.5 text-fg-muted">
                <ClipboardList className="w-3 h-3" /> {c.patient_count} patient{c.patient_count !== 1 ? 's' : ''}
              </div>
            </div>
            <div className="mt-3 flex items-center justify-end gap-2 text-xs">
              <button onClick={() => setEdit(c)} className="text-brand inline-flex items-center gap-1" data-testid={`edit-clinic-${c.clinic_id}`}>
                <Edit2 className="w-3 h-3" /> Edit
              </button>
              <button onClick={() => toggleActive(c)} disabled={busy}
                      className={`inline-flex items-center gap-1 ${c.is_active ? 'text-[#B83A3A]' : 'text-green-700'}`}
                      data-testid={`suspend-clinic-${c.clinic_id}`}>
                {c.is_active ? <><PowerOff className="w-3 h-3" /> Suspend</> : <><Power className="w-3 h-3" /> Activate</>}
              </button>
            </div>
          </div>
        ))}
        {!clinics.length && (
          <div className="col-span-full surface border border-soft rounded-lg p-12 text-center">
            <Building2 className="w-10 h-10 mx-auto text-fg-muted" />
            <p className="text-sm text-fg-muted mt-3">No clinics yet — create your first one.</p>
          </div>
        )}
      </div>

      {edit && <EditModal clinic={edit} onClose={() => setEdit(null)} onSaved={() => { load(); setEdit(null); }} />}
    </div>
  );
}

function EditModal({ clinic, onClose, onSaved }) {
  const isNew = !clinic.clinic_id;
  const [form, setForm] = useState({ name: '', plan: 'free', country_code: '', custom_logo_url: '', custom_tagline: '', custom_brand_color: '', address: '', phone: '', website: '', ...clinic });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        await api.post('/platform/clinics', form);
      } else {
        await api.put(`/platform/clinics/${clinic.clinic_id}`, form);
      }
      onSaved();
    } catch (e) {
      alert('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="surface border border-soft rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="clinic-modal">
        <div className="p-4 border-b border-soft flex items-center justify-between">
          <h2 className="font-heading font-semibold">{isNew ? 'New clinic' : `Edit · ${clinic.name}`}</h2>
          <button onClick={onClose} className="text-fg-muted hover:text-fg"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 grid md:grid-cols-2 gap-3">
          <Field label="Clinic name *" full><input className="input" value={form.name || ''} onChange={e => set('name', e.target.value)} data-testid="c-name" /></Field>
          <Field label="Plan tier">
            <select className="input" value={form.plan} onChange={e => set('plan', e.target.value)} data-testid="c-plan">
              <option value="free">Free</option>
              <option value="starter">Starter</option>
              <option value="growth">Growth</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </Field>
          <Field label="Country code"><input className="input" placeholder="PL, US, GB…" value={form.country_code || ''} onChange={e => set('country_code', e.target.value.toUpperCase().slice(0, 3))} data-testid="c-country" /></Field>
          <Field label="Address" full><input className="input" value={form.address || ''} onChange={e => set('address', e.target.value)} /></Field>
          <Field label="Phone"><input className="input" value={form.phone || ''} onChange={e => set('phone', e.target.value)} /></Field>
          <Field label="Website"><input className="input" value={form.website || ''} onChange={e => set('website', e.target.value)} /></Field>
          <Field label="Logo URL" full><input className="input" value={form.custom_logo_url || ''} onChange={e => set('custom_logo_url', e.target.value)} /></Field>
          <Field label="Tagline" full><input className="input" value={form.custom_tagline || ''} onChange={e => set('custom_tagline', e.target.value)} /></Field>
          <Field label="Brand color (hex)"><input className="input" placeholder="#003D80" value={form.custom_brand_color || ''} onChange={e => set('custom_brand_color', e.target.value)} /></Field>
        </div>

        {!isNew && (
          <FeatureOverridesPanel clinic={clinic} />
        )}

        <div className="px-5 pb-5 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={save} disabled={saving || !form.name} className="btn-primary text-sm" data-testid="c-save">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}


// Per-clinic feature flag overrides. Platform admin can flip a single flag
// per tenant; goes through PUT /platform/clinics/{id}/feature-overrides.
const TOGGLEABLE_FEATURES = [
  ['feature_storefront', 'Product Catalogue'],
  ['feature_membership_hold', 'Patient self-hold on memberships'],
  ['feature_membership_self_renew', 'Patient self-renewal'],
  ['feature_runtime_i18n', 'AI runtime translation (PL/ES)'],
  ['feature_seasonal_theme', 'Seasonal landing theme'],
  ['feature_student_tracker', 'Student / volunteer tracker'],
  ['feature_wellness_checkins', 'At-home wellness check-ins'],
  ['feature_telehealth', 'Telehealth'],
  ['feature_promotions', 'Scheduled promotions'],
  ['feature_marketing', 'Marketing email blasts'],
  ['feature_multi_location', 'Multi-location'],
  ['feature_backup', 'Off-site B2 backups'],
];

function FeatureOverridesPanel({ clinic }) {
  const [overrides, setOverrides] = useState(clinic.feature_overrides || {});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const toggle = (k) => {
    setOverrides(o => {
      const next = { ...o };
      if (k in next) {
        if (next[k] === true) next[k] = false;
        else delete next[k];
      } else {
        next[k] = true;
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await api.put(`/platform/clinics/${clinic.clinic_id}/feature-overrides`, { feature_overrides: overrides });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { alert('Save failed: ' + (e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  return (
    <div className="border-t border-soft p-5" data-testid="feature-overrides-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-xs uppercase tracking-wider text-brand font-semibold">Feature flag overrides</h3>
          <p className="text-[11px] text-fg-muted mt-0.5">Force-on / force-off individual features for this tenant. Unset = inherit defaults.</p>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary text-xs" data-testid="save-overrides-btn">
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save overrides'}
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {TOGGLEABLE_FEATURES.map(([key, label]) => {
          const state = key in overrides ? (overrides[key] === true ? 'on' : 'off') : 'inherit';
          return (
            <div key={key} className="surface border border-soft rounded-md px-3 py-2 flex items-center justify-between gap-2" data-testid={`override-${key}`}>
              <span className="text-xs">{label}</span>
              <div className="flex items-center gap-1">
                {['inherit', 'on', 'off'].map(opt => (
                  <button
                    key={opt}
                    onClick={() => setOverrides(o => {
                      const n = { ...o };
                      if (opt === 'inherit') delete n[key];
                      else n[key] = opt === 'on';
                      return n;
                    })}
                    className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${
                      state === opt
                        ? opt === 'on' ? 'bg-green-600 text-white'
                          : opt === 'off' ? 'bg-[#B83A3A] text-white'
                          : 'bg-brand text-white'
                        : 'text-fg-muted hover:bg-soft'
                    }`}
                    data-testid={`override-${key}-${opt}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return <label className={full ? 'md:col-span-2 block' : 'block'}>
    <div className="text-xs text-fg-muted mb-1">{label}</div>{children}
  </label>;
}
